import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import {
  getGarageVehicles,
  getGarageMakes,
  getGarageModels,
  getGarageYears,
} from "../../../../services/garage";

// Helper function to check if body is empty
const isBodyEmpty = (body) => {
  if (!body) return true;
  if (Array.isArray(body)) return body.length === 0;
  if (typeof body === 'object') return Object.keys(body).length === 0;
  return false;
};

const GarageContext = createContext();

const initialState = {
  baseVehicles: [],
  baseMeta: null,
  basePartyUuid: null,
  vehicles: [],
  vehiclesMeta: null,
  makes: [],
  models: [],
  years: [],
  loading: false,
  filtersLoading: false,
  error: null,
  isEmpty: false,
};

export const GarageProvider = ({ children }) => {
  const [state, setState] = useState(initialState);
  const fetchRequestIdRef = useRef(0);

  const setPartialState = useCallback((updates) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  const fetchVehicles = useCallback(
    async (params = {}, options = {}) => {
      const { persistAsBase = false } = options;
      const requestId = ++fetchRequestIdRef.current;

      setState((prev) => ({
        ...prev,
        loading: true,
        error: null,
        isEmpty: false,
      }));

      try {
        const response = await getGarageVehicles(params);

        if (requestId !== fetchRequestIdRef.current) {
          setState((prev) => ({
            ...prev,
            loading: false,
          }));
          return;
        }

        const responseStatusCode = response?.statusCode;
        const isValidResponse = responseStatusCode === 200 || responseStatusCode === 201;

        if (!isValidResponse) {
          const errorMessage = response?.msgCode?.[0]?.description || 'Invalid response status code';
          throw new Error(errorMessage);
        }

        const body = response?.body;
        
        if (isBodyEmpty(body)) {
          setState((prev) => ({
            ...prev,
            baseVehicles: persistAsBase ? [] : prev.baseVehicles,
            baseMeta: persistAsBase ? null : prev.baseMeta,
            basePartyUuid: persistAsBase ? null : prev.basePartyUuid,
            vehicles: [],
            vehiclesMeta: response.meta || null,
            loading: false,
            error: null,
            isEmpty: true,
          }));
        } else {
          const vehicles = Array.isArray(body) ? body : [];
          const meta = response.meta || null;
          const partyUuid = params?.party_uuid ?? null;

          setState((prev) => ({
            ...prev,
            baseVehicles: persistAsBase ? vehicles : prev.baseVehicles,
            baseMeta: persistAsBase ? meta : prev.baseMeta,
            basePartyUuid: persistAsBase ? partyUuid : prev.basePartyUuid,
            vehicles,
            vehiclesMeta: meta,
            loading: false,
            error: null,
            isEmpty: false,
          }));
        }
      } catch (error) {
        if (requestId !== fetchRequestIdRef.current) {
          setState((prev) => ({
            ...prev,
            loading: false,
          }));
          return;
        }

        console.error("Error fetching garage vehicles:", error);
        setState((prev) => ({
          ...prev,
          loading: false,
          error,
          vehicles: [],
          vehiclesMeta: null,
          isEmpty: false,
          ...(persistAsBase
            ? {
                baseVehicles: [],
                baseMeta: null,
                basePartyUuid: null,
              }
            : {}),
        }));
      }
    },
    []
  );

  const fetchMakes = useCallback(
    async (partyUuid, search = null) => {
      setPartialState({ filtersLoading: true });
      try {
        const response = await getGarageMakes(partyUuid, search);
        setPartialState({
          makes: Array.isArray(response.body) ? response.body : [],
          filtersLoading: false,
        });
      } catch (error) {
        console.error("Error fetching garage makes:", error);
        setPartialState({
          makes: [],
          filtersLoading: false,
        });
      }
    },
    [setPartialState]
  );

  const fetchModels = useCallback(
    async (partyUuid, search = null) => {
      setPartialState({ filtersLoading: true });
      try {
        const response = await getGarageModels(partyUuid, search);
        setPartialState({
          models: Array.isArray(response.body) ? response.body : [],
          filtersLoading: false,
        });
      } catch (error) {
        console.error("Error fetching garage models:", error);
        setPartialState({
          models: [],
          filtersLoading: false,
        });
      }
    },
    [setPartialState]
  );

  const fetchYears = useCallback(
    async (partyUuid, search = null) => {
      setPartialState({ filtersLoading: true });
      try {
        const response = await getGarageYears(partyUuid, search);
        setPartialState({
          years: Array.isArray(response.body) ? response.body : [],
          filtersLoading: false,
        });
      } catch (error) {
        console.error("Error fetching garage years:", error);
        setPartialState({
          years: [],
          filtersLoading: false,
        });
      }
    },
    [setPartialState]
  );

  const setFilteredVehicles = useCallback((vehicles = [], meta = null) => {
    setState((prev) => ({
      ...prev,
      vehicles: Array.isArray(vehicles) ? vehicles : [],
      vehiclesMeta: meta || null,
    }));
  }, []);

  const resetVehiclesToBase = useCallback(() => {
    fetchRequestIdRef.current = fetchRequestIdRef.current + 1;

    setState((prev) => ({
      ...prev,
      vehicles: prev.baseVehicles,
      vehiclesMeta: prev.baseMeta,
      loading: false,
    }));
  }, []);

  const resetVehicles = useCallback(() => {
    setState(initialState);
    fetchRequestIdRef.current = 0;
  }, []);

  const value = {
    baseVehicles: state.baseVehicles,
    baseMeta: state.baseMeta,
    basePartyUuid: state.basePartyUuid,
    vehicles: state.vehicles,
    vehiclesMeta: state.vehiclesMeta,
    makes: state.makes,
    models: state.models,
    years: state.years,
    loading: state.loading,
    filtersLoading: state.filtersLoading,
    error: state.error,
    isEmpty: state.isEmpty,
    fetchVehicles,
    fetchMakes,
    fetchModels,
    fetchYears,
    setFilteredVehicles,
    resetVehiclesToBase,
    resetVehicles,
  };

  return (
    <GarageContext.Provider value={value}>
      {children}
    </GarageContext.Provider>
  );
};


export const useGarage = () => {
  const context = useContext(GarageContext);
  if (!context) {
    throw new Error('useGarage must be used within GarageProvider');
  }
  return context;
};

